export type SettingsSurface = "main" | "phone";

export type SettingsPageKey =
  | "sound"
  | "background"
  | "effects"
  | "debug"
  | "account"
  | "about";

export interface SettingsPageDefinition {
  key: SettingsPageKey;
  mainHeading: "SETTINGS" | "ABOUT";
  mainSubtitle: string;
  phoneTitle: string;
  mainRowHeights: number[];
  mainGapVariant?: "sound";
}

export interface SettingsPageOptions {
  surface: SettingsSurface;
  canUseDebugSettings: boolean;
}

const SETTINGS_PAGE_DEFINITIONS: Record<SettingsPageKey, SettingsPageDefinition> = {
  sound: {
    key: "sound",
    mainHeading: "SETTINGS",
    mainSubtitle: "SOUND & FEEL",
    phoneTitle: "SOUND & FX",
    mainRowHeights: [36, 52, 52, 36, 36],
    mainGapVariant: "sound",
  },
  background: {
    key: "background",
    mainHeading: "SETTINGS",
    mainSubtitle: "BACKGROUND",
    phoneTitle: "BACKGROUND",
    mainRowHeights: [30, 30, 30, 30, 30],
  },
  effects: {
    key: "effects",
    mainHeading: "SETTINGS",
    mainSubtitle: "EFFECTS",
    phoneTitle: "EFFECTS",
    mainRowHeights: [30, 30, 30, 30, 30],
  },
  debug: {
    key: "debug",
    mainHeading: "SETTINGS",
    mainSubtitle: "DEBUG",
    phoneTitle: "DEBUG",
    mainRowHeights: [30, 30, 30, 30, 30, 30, 30],
  },
  account: {
    key: "account",
    mainHeading: "SETTINGS",
    mainSubtitle: "ACCOUNT",
    phoneTitle: "ACCOUNT",
    mainRowHeights: [],
  },
  about: {
    key: "about",
    mainHeading: "ABOUT",
    mainSubtitle: "",
    phoneTitle: "ABOUT",
    mainRowHeights: [],
  },
};

export function getSettingsPages(options: SettingsPageOptions): SettingsPageDefinition[] {
  if (options.surface === "phone") {
    return [
      SETTINGS_PAGE_DEFINITIONS.sound,
      SETTINGS_PAGE_DEFINITIONS.background,
      SETTINGS_PAGE_DEFINITIONS.effects,
      SETTINGS_PAGE_DEFINITIONS.account,
      SETTINGS_PAGE_DEFINITIONS.about,
    ];
  }

  return [
    SETTINGS_PAGE_DEFINITIONS.sound,
    SETTINGS_PAGE_DEFINITIONS.background,
    SETTINGS_PAGE_DEFINITIONS.effects,
    ...(options.canUseDebugSettings ? [SETTINGS_PAGE_DEFINITIONS.debug] : []),
    SETTINGS_PAGE_DEFINITIONS.about,
  ];
}

export function getSettingsPageCount(options: SettingsPageOptions): number {
  return getSettingsPages(options).length;
}

export function getSettingsPageDefinition(
  options: SettingsPageOptions,
  pageIndex: number,
): SettingsPageDefinition {
  const pages = getSettingsPages(options);
  const clampedIndex = Math.min(Math.max(0, pageIndex), pages.length - 1);
  return pages[clampedIndex] ?? SETTINGS_PAGE_DEFINITIONS.about;
}
